import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import type { ComponentType, ReactElement } from "react";
import { useAuthStore } from "./core/stores/useAuthStore";
import { RouteErrorScreen } from "./core/components/RouteErrorScreen";
import { InquiryFormErrorScreen } from "./features/inquiry-form/routes/InquiryFormErrorScreen";

function lazyScreen<K extends string, M extends Record<K, ComponentType>>(
  loader: () => Promise<M>,
  exportName: K,
): { lazy: () => Promise<{ Component: ComponentType }> } {
  return {
    lazy: async () => ({ Component: (await loader())[exportName] }),
  };
}

function AuthGuard(): ReactElement | null {
  const location = useLocation();
  const session = useAuthStore((s) => s.session);
  const initialized = useAuthStore((s) => s.initialized);
  if (!initialized) return <OpeningBakeryScreen />;
  if (!session)
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search + location.hash }}
      />
    );
  return <Outlet />;
}

function OpeningBakeryScreen(): ReactElement {
  return (
    <div
      role="status"
      className="grid min-h-dvh place-items-center bg-background p-8 text-sm text-muted-foreground"
    >
      Opening your bakery…
    </div>
  );
}

// The retired composer's edit URL opens the order view.
function OrderViewRedirect(): ReactElement {
  const { id = "" } = useParams();
  return <Navigate to={`/orders/${id}/view`} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    hydrateFallbackElement: <OpeningBakeryScreen />,
    ...lazyScreen(
      () => import("./features/auth/routes/LoginWorkspaceScreen"),
      "LoginWorkspaceScreen",
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    // Public client-facing inquiry form — no auth, no nav scaffold, own theme.
    // Branded, client-safe error screen (never the raw router default).
    path: "/i/:slug",
    errorElement: <InquiryFormErrorScreen />,
    ...lazyScreen(
      () => import("./features/inquiry-form/routes/InquiryFormScreen"),
      "InquiryFormScreen",
    ),
  },
  {
    element: <AuthGuard />,
    hydrateFallbackElement: <OpeningBakeryScreen />,
    errorElement: <RouteErrorScreen />,
    children: [
      {
        ...lazyScreen(() => import("./core/components/AppShell"), "AppShell"),
        children: [
          { index: true, element: <Navigate to="/orders" replace /> },
          {
            path: "/orders",
            ...lazyScreen(
              () => import("./features/orders/routes/OrderBoardScreen"),
              "OrderBoardScreen",
            ),
          },
          ...(import.meta.env.DEV
            ? [
                {
                  path: "/design-system",
                  ...lazyScreen(
                    () => import("./features/design-system/routes/ComponentCatalogue"),
                    "ComponentCatalogue",
                  ),
                },
              ]
            : []),
          {
            path: "/orders/lab/new",
            ...lazyScreen(
              () => import("./features/orders/routes/OrderWorkspaceScreen"),
              "OrderWorkspaceScreen",
            ),
          },
          {
            path: "/orders/:id/view",
            ...lazyScreen(
              () => import("./features/orders/routes/OrderWorkspaceScreen"),
              "OrderWorkspaceScreen",
            ),
          },
          {
            path: "/orders/:id/revise",
            ...lazyScreen(
              () => import("./features/orders/routes/OrderWorkspaceScreen"),
              "OrderWorkspaceScreen",
            ),
          },
          // Retired legacy composer URLs.
          { path: "/orders/new", element: <Navigate to="/orders/lab/new" replace /> },
          { path: "/orders/:id/edit", element: <OrderViewRedirect /> },
          {
            path: "/inquiries",
            ...lazyScreen(
              () => import("./features/inquiries/routes/InquiryInboxScreen"),
              "InquiryInboxScreen",
            ),
          },
          {
            path: "/inquiries/forms",
            ...lazyScreen(
              () => import("./features/inquiries/routes/InquiryFormsScreen"),
              "InquiryFormsScreen",
            ),
          },
          {
            path: "/inquiries/forms/:id",
            ...lazyScreen(
              () => import("./features/inquiries/routes/InquiryFormScreen"),
              "InquiryFormScreen",
            ),
          },
          {
            path: "/inquiries/:id",
            ...lazyScreen(
              () => import("./features/inquiries/routes/InquiryViewScreen"),
              "InquiryViewScreen",
            ),
          },
          {
            path: "/inquiries/templates/new",
            ...lazyScreen(
              () => import("./features/inquiries/routes/FormEditorScreen"),
              "FormEditorScreen",
            ),
          },
          {
            path: "/inquiries/templates/:id/edit",
            ...lazyScreen(
              () => import("./features/inquiries/routes/FormEditorScreen"),
              "FormEditorScreen",
            ),
          },
          {
            path: "/recipes",
            ...lazyScreen(
              () => import("./features/recipes/routes/RecipeLibraryScreen"),
              "RecipeLibraryScreen",
            ),
          },
          {
            path: "/recipes/new",
            ...lazyScreen(
              () => import("./features/recipes/routes/RecipeEditorScreen"),
              "RecipeEditorScreen",
            ),
          },
          {
            path: "/recipes/:id/edit",
            ...lazyScreen(
              () => import("./features/recipes/routes/RecipeEditorScreen"),
              "RecipeEditorScreen",
            ),
          },
          {
            path: "/recipes/:id/view",
            ...lazyScreen(
              () => import("./features/recipes/routes/RecipeDetailScreen"),
              "RecipeDetailScreen",
            ),
          },
          {
            path: "/pantry",
            ...lazyScreen(
              () => import("./features/pantry/routes/PantryLibraryScreen"),
              "PantryLibraryScreen",
            ),
          },
          {
            path: "/pantry/new",
            ...lazyScreen(
              () => import("./features/pantry/routes/PantryEditorScreen"),
              "PantryEditorScreen",
            ),
          },
          {
            path: "/pantry/:id/edit",
            ...lazyScreen(
              () => import("./features/pantry/routes/PantryEditorScreen"),
              "PantryEditorScreen",
            ),
          },
          {
            path: "/pantry/:id/view",
            ...lazyScreen(
              () => import("./features/pantry/routes/PantryDetailScreen"),
              "PantryDetailScreen",
            ),
          },
          {
            path: "/packaging",
            ...lazyScreen(
              () => import("./features/packaging/routes/PackagingLibraryScreen"),
              "PackagingLibraryScreen",
            ),
          },
          {
            path: "/packaging/new",
            ...lazyScreen(
              () => import("./features/packaging/routes/PackagingEditorScreen"),
              "PackagingEditorScreen",
            ),
          },
          {
            path: "/packaging/:id/edit",
            ...lazyScreen(
              () => import("./features/packaging/routes/PackagingEditorScreen"),
              "PackagingEditorScreen",
            ),
          },
          {
            path: "/packaging/:id/view",
            ...lazyScreen(
              () => import("./features/packaging/routes/PackagingDetailScreen"),
              "PackagingDetailScreen",
            ),
          },
          {
            path: "/cake-pans",
            ...lazyScreen(
              () => import("./features/pans/routes/PanLibraryScreen"),
              "PanLibraryScreen",
            ),
          },
          {
            path: "/cake-pans/new",
            ...lazyScreen(
              () => import("./features/pans/routes/PanEditorScreen"),
              "PanEditorScreen",
            ),
          },
          {
            path: "/cake-pans/:id/edit",
            ...lazyScreen(
              () => import("./features/pans/routes/PanEditorScreen"),
              "PanEditorScreen",
            ),
          },
          {
            path: "/cake-pans/:id/view",
            ...lazyScreen(
              () => import("./features/pans/routes/PanDetailScreen"),
              "PanDetailScreen",
            ),
          },
          {
            path: "/baking",
            ...lazyScreen(
              () => import("./features/baking/routes/BakingHomeScreen"),
              "BakingHomeScreen",
            ),
          },
          {
            path: "/baking/order/:orderId",
            ...lazyScreen(
              () => import("./features/baking/routes/BakingWorkspaceScreen"),
              "BakingWorkspaceScreen",
            ),
          },
          {
            path: "/baking/recipes",
            ...lazyScreen(
              () => import("./features/baking/routes/BakingWorkspaceScreen"),
              "BakingWorkspaceScreen",
            ),
          },
          {
            path: "/settings",
            ...lazyScreen(
              () => import("./features/settings/routes/SettingsWorkspaceScreen"),
              "SettingsWorkspaceScreen",
            ),
          },
        ],
      },
      {
        // The owner's full-screen preview of a form, outside the app shell.
        path: "/inquiries/forms/:id/preview",
        ...lazyScreen(
          () => import("./features/inquiry-form/routes/InquiryFormPreviewScreen"),
          "InquiryFormPreviewScreen",
        ),
      },
    ],
  },
]);
